import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductNombreRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} help
 * @property {HTMLElement} error
 */

const SUBMIT_CONTROL_SELECTOR =
  'button[type="submit"], button[name="add"], add-to-cart-component, .shopify-payment-button';

/**
 * Custom name field that must match the selected "Cantidad de letras" variant option.
 *
 * Document-level capture listeners are registered at module load (before product-form.js)
 * so validation can stopImmediatePropagation before AddToCartComponent.handleClick animates.
 *
 * @extends {Component<ProductNombreRefs>}
 */
class ProductNombreField extends Component {
  requiredRefs = ['input', 'help', 'error'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.refs.input.addEventListener('input', this.#onInput, { signal });

    this.#syncExpectedLetters(this.dataset.expectedLetters);
    this.#clearError();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Public validation used by document capture handlers.
   * @returns {boolean}
   */
  validateForSubmit() {
    return this.#validate();
  }

  focusInput() {
    this.refs.input.focus();
  }

  #onInput = () => {
    this.#clearError();
    if (this.refs.input.value.length > 0) {
      this.#validate({ silent: true });
    }
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail?.data) return;

    if (detail.data.newProduct) {
      this.dataset.productId = detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const optionIndex = this.dataset.letterOptionIndex;
    if (optionIndex === undefined || optionIndex === '') return;

    const resource = detail.resource;
    if (!resource) return;

    const options = [resource.option1, resource.option2, resource.option3];
    const expected = options[Number(optionIndex)];
    this.#syncExpectedLetters(expected);
    this.#clearError();
  };

  /**
   * @param {string | null | undefined} expected
   */
  #syncExpectedLetters(expected) {
    const parsed = expected != null && expected !== '' ? Number.parseInt(String(expected), 10) : NaN;

    if (Number.isFinite(parsed) && parsed > 0) {
      this.dataset.expectedLetters = String(parsed);
      this.refs.help.hidden = false;
      this.refs.help.textContent = `Debe tener exactamente ${parsed} letras`;
    } else {
      delete this.dataset.expectedLetters;
      this.refs.help.hidden = true;
      this.refs.help.textContent = '';
    }
  }

  /**
   * Letter count excludes whitespace.
   * @param {string} value
   */
  #countLetters(value) {
    return value.replace(/\s/g, '').length;
  }

  #expectedLetters() {
    const raw = this.dataset.expectedLetters;
    if (raw === undefined || raw === '') return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  /**
   * @param {{ silent?: boolean }} [options]
   * @returns {boolean}
   */
  #validate({ silent = false } = {}) {
    const { input } = this.refs;
    const value = input.value.trim();
    const required = this.dataset.required === 'true';
    const expected = this.#expectedLetters();
    const letterCount = this.#countLetters(value);

    if (required && value === '') {
      if (!silent) this.#showError('Escribe el nombre');
      input.setAttribute('aria-invalid', 'true');
      return false;
    }

    if (expected != null && (required || value !== '') && letterCount !== expected) {
      if (!silent) {
        this.#showError(`El nombre debe tener exactamente ${expected} letras`);
      }
      input.setAttribute('aria-invalid', 'true');
      return false;
    }

    this.#clearError();
    return true;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { error, input } = this.refs;
    error.hidden = false;
    error.textContent = message;
    input.setAttribute('aria-invalid', 'true');
  }

  #clearError() {
    const { error, input } = this.refs;
    error.hidden = true;
    error.textContent = '';
    input.removeAttribute('aria-invalid');
  }
}

/**
 * @param {Element} target
 * @returns {ProductNombreField | null}
 */
function findNombreFieldForTarget(target) {
  const form = target.closest('form[data-type="add-to-cart-form"], form.shopify-product-form');
  if (!form) return null;

  const field = form.querySelector('product-nombre-field');
  return field instanceof ProductNombreField ? field : null;
}

/**
 * Undo ATC success animation if it already started.
 * @param {Element} target
 */
function revertAddToCartAnimation(target) {
  const form = target.closest('form');
  const button =
    form?.querySelector('add-to-cart-component .add-to-cart-button, button.add-to-cart-button') ??
    target.closest('.add-to-cart-button');

  if (button instanceof HTMLElement) {
    button.classList.remove('atc-added');
  }

  document.querySelectorAll('fly-to-cart').forEach((el) => el.remove());
}

/**
 * @param {MouseEvent} event
 */
function onDocumentClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (!target.closest(SUBMIT_CONTROL_SELECTOR)) return;

  const field = findNombreFieldForTarget(target);
  if (!field) return;

  if (field.validateForSubmit()) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  revertAddToCartAnimation(target);
  field.focusInput();
}

/**
 * @param {KeyboardEvent} event
 */
function onDocumentKeydown(event) {
  if (event.key !== 'Enter') return;

  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;

  const field = target.closest('product-nombre-field');
  if (!(field instanceof ProductNombreField)) return;
  if (target.name !== 'properties[Nombre]') return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (!field.validateForSubmit()) {
    field.focusInput();
    return;
  }

  const submitButton = field.closest('form')?.querySelector(
    'button[type="submit"][name="add"], add-to-cart-component button'
  );
  if (submitButton instanceof HTMLElement) {
    submitButton.click();
  }
}

// Register before product-form's document capture listeners (script loads first in scripts.liquid).
document.addEventListener('click', onDocumentClick, true);
document.addEventListener('keydown', onDocumentKeydown, true);

if (!customElements.get('product-nombre-field')) {
  customElements.define('product-nombre-field', ProductNombreField);
}
